import React from 'react';
import PropTypes from 'prop-types';
import { determineBoardObj } from '../../helpers';
import { SHORT_NAME_MAP, RESPONSES } from '../../constants/question-data-map';
import { BCMR, DRB } from '../../constants';
import VABenefitsAccordion from '../VABenefitsAccordion';

const AdditionalInstructions = ({ formResponses }) => {
  const serviceBranch = formResponses[SHORT_NAME_MAP.SERVICE_BRANCH];
  const { abbr } = determineBoardObj(formResponses);

  return (
    <section>
      <va-accordion>
        <va-accordion-item header="What happens after I submit my application?">
          <p>
            The Board reviews nearly all applications within 18 months. You can
            continue to submit supporting documentation until the Board is
            finished reviewing your application.
          </p>
          <p>
            If the Board approves your application, they’ll tell your service
            personnel office to issue you either a DD Form 215, which updates
            your existing DD214, or a new DD214. If you get a new DD214, make
            sure to ask for a copy of your records.
            <p>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.archives.gov/veterans/military-service-records"
              >
                Request a copy of your own records (opens in a new tab)
              </a>
            </p>
          </p>
          <p>
            If they change your discharge to honorable, you’re immediately
            considered an eligible VA Veteran. This means you can apply for VA
            benefits and services. For now, you may still apply for VA
            eligibility by requesting a Character of Discharge review.
          </p>
          <p>
            <strong>Note:</strong> You can ask for a VA Character of Discharge
            review and apply for a discharge upgrade from the DOD or Coast Guard
            at the same time.
          </p>
        </va-accordion-item>
        <VABenefitsAccordion isResultsPage />
      </va-accordion>
      <h2>Additional Resources</h2>
      <hr />
      <ul className="additional-resources">
        <li>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="/health-care/health-needs-conditions/military-sexual-trauma/"
          >
            VA health benefits for Veterans who experience military sexual
            trauma (opens in a new tab)
          </a>
        </li>
        <li>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="/health-care/health-needs-conditions/mental-health/"
          >
            VA health benefits for Veterans with mental health conditions (opens
            in a new tab)
          </a>
        </li>
        <li>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="/health-care/health-needs-conditions/mental-health/ptsd/"
          >
            VA health benefits for Veterans with PTSD (opens in a new tab)
          </a>
        </li>
        <li>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.benefits.va.gov/BENEFITS/docs/COD_Factsheet.pdf"
          >
            VA Guidance on Character of Discharge Reviews (opens in a new tab)
          </a>
        </li>
        {serviceBranch === RESPONSES.ARMY && (
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.army.mil/arba"
            >
              Army Review Boards Agency (opens in a new tab)
            </a>
          </li>
        )}
        {serviceBranch === RESPONSES.ARMY &&
          determineBoardObj(formResponses).abbr === DRB && (
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.army.mil/arba#org-Discharge-Review"
              >
                Army Discharge Review Board (opens in a new tab)
              </a>
            </li>
          )}
        {serviceBranch === RESPONSES.ARMY &&
          abbr === BCMR && (
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.army.mil/arba#org-other-military-records-corrections"
              >
                Army Board for Correction of Military Records (opens in a new
                tab)
              </a>
            </li>
          )}
        {[RESPONSES.NAVY, RESPONSES.MARINE_CORPS].includes(serviceBranch) && (
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://www.secnav.navy.mil/mra/CORB/pages/ndrb/default.aspx"
            >
              Naval Discharge Review Board (opens in a new tab)
            </a>
          </li>
        )}
        {(serviceBranch === RESPONSES.AIR_FORCE ||
          serviceBranch === RESPONSES.SPACE_FORCE) &&
          abbr === BCMR && (
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="http://www.afpc.af.mil/Board-for-Correction-of-Military-Records/"
              >
                Air Force Board for Correction of Military Records (opens in a
                new tab)
              </a>
            </li>
          )}
        {serviceBranch === RESPONSES.COAST_GUARD &&
          abbr === BCMR && (
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.uscg.mil/Resources/legal/BCMR/"
              >
                Coast Guard Board for Correction of Military Records (opens in a
                new tab)
              </a>
            </li>
          )}
        {serviceBranch === RESPONSES.COAST_GUARD &&
          abbr === DRB && (
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.uscg.mil/Resources/Legal/DRB.aspx/"
              >
                Coast Guard Discharge Review Board (opens in a new tab)
              </a>
            </li>
          )}
      </ul>
      <p>
        <strong>Note:</strong> This information is based on how you answered the
        questions on the previous page. This information won’t be specific to
        someone with different answers than you.
      </p>
    </section>
  );
};

AdditionalInstructions.propTypes = {
  formResponses: PropTypes.object.isRequired,
};

export default AdditionalInstructions;
